import { originBase } from "../../rules/origin.js";
import type { EmbeddedStudioDefaultProps } from "../../rules/types.js";
import {
  type EmbedConfig,
  embedSearchSchema,
  resolveEmbedConfig,
} from "../../search/rules/params.js";
import { REQUEST_SESSION_TOKEN_MESSAGE, SET_SESSION_TOKEN_MESSAGE } from "./bridgeMessages.js";
import { ALLOWLIST_WILDCARD, allowlistUnrestricted } from "./safetyRules.js";

/**
 * EmbedConfigFieldKey is the subset of EmbedConfig keys the embed wizard exposes
 * as configuration: the keys EMBED_CONFIG_FIELDS pairs with a URL param. It
 * deliberately excludes the content selectors (the itinerary/collection/zone ids,
 * the activity, the overlay, the time offset) and the non-configurable `embedded`
 * flag. So a descriptor can only ever name a field the snippet builder is allowed
 * to override.
 */
export type EmbedConfigFieldKey =
  | "preferredLanguage"
  | "preferredTheme"
  | "preferredUnitLength"
  | "preferredUnitPace"
  | "preferredUnitSlope"
  | "preferredUnitTemperature"
  | "segmentsColor"
  | "terrain"
  | "terrainVariant"
  | "withDirectionsControl"
  | "withDownloadControl"
  | "withNavigationControl"
  | "withOverlays"
  | "withOverlaysControl"
  | "withPreferencesControl"
  | "withProfileAnalysis"
  | "withProfileCharacteristics"
  | "withProfileElevation"
  | "withProfilePaceControl"
  | "withStreetviewControl"
  | "withTerrains";

/**
 * EmbedConfigFieldKind tags how a descriptor's value serializes into the URL: a
 * `boolean` flag becomes `"true"`/`"false"`, an `enum` or `string` becomes its
 * own text, and a `stringArray` emits one repeated param per element. The kind
 * also tells the diff how to compare a value against the organization default (a scalar
 * compare for the first three, an order-insensitive contents compare for the
 * array).
 */
export type EmbedConfigFieldKind = "boolean" | "enum" | "string" | "stringArray";

/**
 * EmbedConfigField pairs one configurable EmbedConfig field with the snake_case
 * search-param name the embed page reads and the kind that drives both its
 * serialization and its default-diff. The `key` is constrained to
 * EmbedConfigFieldKey, so a descriptor cannot reference a content selector or a
 * non-EmbedConfig field. The list of these (EMBED_CONFIG_FIELDS) is the shared
 * source of truth the URL serializer and the wizard's form controls both drive
 * from.
 */
export interface EmbedConfigField {
  /** key is the EmbedConfig field this descriptor configures. */
  key: EmbedConfigFieldKey;
  /** kind tags how the value serializes and how it is diffed against the default. */
  kind: EmbedConfigFieldKind;
  /**
   * param is the snake_case search-param name the embed reads for this field, which is also the
   * field's key in the organization's default props.
   */
  param: keyof EmbeddedStudioDefaultProps;
}

/**
 * EMBED_CONFIG_FIELDS is the ordered descriptor list pairing each configurable
 * EmbedConfig field with its embed search-param name and kind. It is the shared
 * vocabulary the snippet serializer (buildEmbedSearch) and the wizard form
 * controls both drive from, so the params a snippet can override and the controls
 * the form renders never drift. The order is grouped by configuration section
 * (Appearance, Map controls, Profile, Layers) and, within each section,
 * follows the order the controls render on screen (the top-right control stack for
 * Map controls, the elevation panel top-to-bottom for Profile), so a section's rail
 * order, the serialized search, and the rendered surface all read the same. The
 * content selectors and the always-on `embedded` flag are intentionally absent: a
 * snippet authors the display config, not the content selection (that comes from the
 * itinerary the wizard opened for) and not the always-on flag.
 */
export const EMBED_CONFIG_FIELDS: readonly EmbedConfigField[] = [
  // Appearance.
  { key: "preferredLanguage", kind: "enum", param: "preferred_language" },
  { key: "preferredTheme", kind: "enum", param: "preferred_theme" },
  { key: "preferredUnitLength", kind: "enum", param: "preferred_unit_length" },
  { key: "preferredUnitPace", kind: "enum", param: "preferred_unit_pace" },
  { key: "preferredUnitSlope", kind: "enum", param: "preferred_unit_slope" },
  { key: "preferredUnitTemperature", kind: "enum", param: "preferred_unit_temperature" },
  { key: "terrain", kind: "string", param: "terrain" },
  { key: "terrainVariant", kind: "enum", param: "terrain_variant" },
  { key: "segmentsColor", kind: "string", param: "segments_color" },
  // Map controls, in the top-right stack's render order: zoom, preferences, download,
  // directions, Street View (the overlay time slider is in the Layers section below).
  { key: "withNavigationControl", kind: "boolean", param: "with_navigation_control" },
  { key: "withPreferencesControl", kind: "boolean", param: "with_preferences_control" },
  { key: "withDownloadControl", kind: "boolean", param: "with_download_control" },
  { key: "withDirectionsControl", kind: "boolean", param: "with_directions_control" },
  { key: "withStreetviewControl", kind: "boolean", param: "with_streetview_control" },
  // Profile panel, in the elevation panel's render order: characteristics, elevation,
  // analysis.
  { key: "withProfileCharacteristics", kind: "boolean", param: "with_profile_characteristics" },
  { key: "withProfileElevation", kind: "boolean", param: "with_profile_elevation" },
  { key: "withProfilePaceControl", kind: "boolean", param: "with_profile_pace_control" },
  { key: "withProfileAnalysis", kind: "boolean", param: "with_profile_analysis" },
  // Layers: the layers-button toggle that governs the panel, then the terrain and
  // overlay allowlists it offers.
  { key: "withOverlaysControl", kind: "boolean", param: "with_overlays_control" },
  { key: "withTerrains", kind: "stringArray", param: "with_terrains" },
  { key: "withOverlays", kind: "stringArray", param: "with_overlays" },
] as const;

/**
 * emptyEmbedSearch is the parsed empty search every embed field falls through,
 * so resolveEmbedDefaults resolves each field to the organization default rather than a URL
 * override. It is the merged embed schema applied to an empty object: every field
 * is optional there, so the parse yields all-undefined and the resolver takes the
 * default_props branch for every field. Parsed once and frozen because it is a
 * constant input, never mutated.
 */
const emptyEmbedSearch = embedSearchSchema.parse({});

/**
 * resolveEmbedDefaults resolves the organization's default_props into the fully-resolved
 * EmbedConfig the wizard form initializes to and the Reset action returns to. It
 * folds an empty search over the default_props, so every field falls to the organization
 * default (or the hardcoded fallback when the organization leaves it unset): the same
 * precedence the live embed page applies, minus any URL override. It is the
 * baseline buildEmbedSearch diffs against: a configured value equal to this
 * default emits no param.
 *
 * @param defaultProps The organization's typed defaults, as `normalizeDefaultProps` answers
 *   them.
 */
export const resolveEmbedDefaults = (defaultProps: EmbeddedStudioDefaultProps): EmbedConfig =>
  resolveEmbedConfig(emptyEmbedSearch, defaultProps);

/**
 * arraysDifferByContents reports whether two string arrays differ by their
 * contents, ignoring order: the diff a stringArray descriptor uses so a
 * reordered-but-equal overlay/terrain list is not treated as an override. Compares
 * by length and a sorted element-wise walk, so `["a","b"]` and `["b","a"]` are
 * equal while `["a"]` and `["a","b"]` differ.
 */
const arraysDifferByContents = (a: readonly string[], b: readonly string[]): boolean => {
  if (a.length !== b.length) {
    return true;
  }

  const sortedA = [...a].sort();
  const sortedB = [...b].sort();

  return sortedA.some((value, index) => value !== sortedB[index]);
};

/**
 * allowlistEntries emits the pairs one display allowlist contributes, against the default it
 * overrides. Every list field of an embed's config is one (`with_terrains`, `with_overlays`).
 *
 * - A list that offers the whole catalog (empty, or holding the wildcard) emits the wildcard
 *   alone, and nothing when the default offers the whole catalog too. The wildcard is how a URL
 *   states a list that restricts nothing: a repeated param has one pair per entry, and an empty
 *   list has none. Left out, an empty list would resolve to the organization's own list, and
 *   the embed would restrict to it while the form shows every layer.
 * - A list that names slugs emits one pair per slug, and nothing when the default names the
 *   same slugs in any order.
 */
const allowlistEntries = (
  param: string,
  value: readonly string[],
  fallback: readonly string[],
): Array<[string, string]> => {
  if (allowlistUnrestricted(value)) {
    return allowlistUnrestricted(fallback) ? [] : [[param, ALLOWLIST_WILDCARD]];
  }

  return arraysDifferByContents(value, fallback) ? value.map((slug) => [param, slug]) : [];
};

/**
 * fieldEntries emits the ordered [param, value] pairs a single descriptor
 * contributes when its configured value differs from the resolved default, or an
 * empty array when it matches (a value equal to the default is never emitted: a
 * param exists solely to override the organization default). A boolean serializes to
 * "true"/"false"; an enum/string serializes to its own text; a list is a display
 * allowlist, emitted as {@link allowlistEntries} states.
 *
 * The value decides how it serializes, read off the config itself, so no member is taken for
 * another type on a descriptor's word. `snippet.test.ts` holds each descriptor's kind to the
 * type of the value it configures.
 */
const fieldEntries = (
  field: EmbedConfigField,
  config: EmbedConfig,
  defaults: EmbedConfig,
): Array<[string, string]> => {
  const value = config[field.key];
  const fallback = defaults[field.key];

  if (Array.isArray(value)) {
    return allowlistEntries(field.param, value, Array.isArray(fallback) ? fallback : []);
  }

  if (value === fallback) {
    return [];
  }

  return [[field.param, typeof value === "boolean" ? (value ? "true" : "false") : value]];
};

/**
 * BuildEmbedSearchInput is the input to buildEmbedSearch: the publishable key the
 * snippet always carries, the content selection (a collection, an itinerary, or
 * both), the optional session token (present only for an organization that sets
 * require_session_token), the live config the wizard authored, and the organization's
 * resolved defaults to diff against. At least one of collectionId / itineraryId names what
 * the embed renders; a caller with neither builds a snippet with no content, which the embed
 * page answers with its own empty state.
 */
export interface BuildEmbedSearchInput {
  /**
   * collectionId is the collection the embed renders. Alone it selects the whole
   * collection; alongside an itinerary it frames the collection on that one.
   * Omitted from the search when absent.
   */
  collectionId?: string | undefined;
  /** config is the live embed configuration the wizard authored. */
  config: EmbedConfig;
  /** defaults is the organization's resolved defaults the config is diffed against. */
  defaults: EmbedConfig;
  /**
   * itineraryId is the itinerary the embed renders, alone or as the frame inside a
   * collection. Omitted from the search when the embed targets a whole collection.
   */
  itineraryId?: string | undefined;
  /** publishableKey names the organization the embed is served for. */
  publishableKey: string;
  /**
   * sessionToken is the optional embed session token for an organization that sets
   * require_session_token; omitted from the search when absent.
   */
  sessionToken?: string | undefined;
}

/**
 * buildEmbedSearch builds the ordered embed search-param pairs the snippet's iframe
 * src carries. The order is deterministic for testability: the publishable key
 * first, then the itinerary id when present, then the collection id when present,
 * then the session token when present, then, for each descriptor in EMBED_CONFIG_FIELDS in
 * list order, a param ONLY when the configured value differs from the resolved
 * default. A value equal to the organization default is never emitted, so the snippet stays
 * minimal and a publisher's URL carries only the overrides they actually changed.
 * Booleans serialize to "true"/"false"; an allowlist emits one repeated entry per slug
 * and differs by contents (order-insensitive), and one that offers the whole catalog
 * emits the wildcard `*`, which the embed reads as no restriction.
 *
 * @param input The key, the content, the session token, the config and the defaults it is
 *   diffed against.
 * @returns The `[param, value]` pairs, not a string, so the caller renders them through
 *   URLSearchParams (see {@link buildEmbedSrc}).
 */
export const buildEmbedSearch = (input: BuildEmbedSearchInput): Array<[string, string]> => {
  const entries: Array<[string, string]> = [["publishable_key", input.publishableKey]];

  // Either content selector may stand alone. An itinerary alone embeds that itinerary; a
  // collection alone embeds the whole folder; together they embed the collection
  // framed on the one itinerary.
  if (input.itineraryId) {
    entries.push(["itinerary_id", input.itineraryId]);
  }

  if (input.collectionId) {
    entries.push(["collection_id", input.collectionId]);
  }

  if (input.sessionToken) {
    entries.push(["session_token", input.sessionToken]);
  }

  for (const field of EMBED_CONFIG_FIELDS) {
    entries.push(...fieldEntries(field, input.config, input.defaults));
  }

  return entries;
};

/**
 * buildEmbedSrc renders the ordered search pairs into the embed iframe's src: the
 * `${origin}/studio/embed` path with the params serialized through URLSearchParams (which
 * percent-encodes each value), plus an optional URL `hash` fragment appended verbatim (the
 * `#view=…` anchor {@link buildEmbedViewHash} writes).
 *
 * @param origin The app origin the embed is served from, a scheme and a host with no path. A
 *   trailing `/` is read as none. The caller supplies it, so the builder reads no environment.
 * @param search The ordered pairs {@link buildEmbedSearch} answers.
 * @param hash The fragment to append, `#` included.
 * @default hash ""
 */
export const buildEmbedSrc = (
  origin: string,
  search: Array<[string, string]>,
  hash = "",
): string => {
  const params = new URLSearchParams(search);

  return `${originBase(origin)}/studio/embed?${params.toString()}${hash}`;
};

/**
 * EmbedViewAnchor is the captured per-embed default camera buildEmbedViewHash encodes
 * (each channel null when the publisher left it unset). It mirrors the wizard's
 * DefaultView shape structurally, so this module imports no type of the wizard's.
 */
export interface EmbedViewAnchor {
  bearing: number | null;
  center: [number, number] | null;
  pitch: number | null;
  zoom: number | null;
}

/**
 * buildEmbedViewHash encodes the captured default view as MapLibre's `#view` URL hash,
 * `#view=<zoom>/<lat>/<lng>/<bearing>/<pitch>`: the exact anchor a `hash: "view"` map
 * reads on load to open framed there. The publisher captures it from the live preview,
 * and it rides the copyable snippet as this hash (NOT search params), so the embedded
 * map frames itself purely off the URL fragment. center + zoom are the essential anchor;
 * with either unset there is nothing to frame, so the hash is empty (the embed opens at
 * its plain fit-to-itinerary). A null bearing/pitch encodes as 0 (north-up / flat). Pure
 * string output (no DOM, no environment), so it stays a testable leaf.
 *
 * @param view The captured camera, each channel null when the publisher left it unset.
 * @returns The fragment, `#` included, or the empty string when the view frames nothing.
 *
 * @example
 * ```ts
 * const hash = buildEmbedViewHash({ bearing: 45, center: [6.86, 45.83], pitch: 30, zoom: 11 });
 * // hash is "#view=11.00/45.830000/6.860000/45/30"
 * ```
 */
export const buildEmbedViewHash = (view: EmbedViewAnchor): string => {
  if (view.center === null || view.zoom === null) {
    return "";
  }

  const [lng, lat] = view.center;
  const bearing = Math.round(view.bearing ?? 0);
  const pitch = Math.round(view.pitch ?? 0);

  return `#view=${view.zoom.toFixed(2)}/${lat.toFixed(6)}/${lng.toFixed(6)}/${bearing}/${pitch}`;
};

/**
 * EMBED_IFRAME_RADIUS_PX is the corner radius, in px, shared by the copy-paste snippet
 * (buildEmbedIframe) and the wizard's in-app preview box, so the preview frames the embed
 * exactly as the published iframe renders it. It is a concrete px length, NOT a Mantine
 * radius token: the snippet runs on the publisher's own page where no Mantine CSS variables
 * resolve, so the value must be self-contained. 8px is the studio's `md` radius, so the
 * embed reads as a finished card on the host page.
 */
export const EMBED_IFRAME_RADIUS_PX = 8;

/**
 * BuildEmbedIframeInput is the input to buildEmbedIframe: the iframe src and the
 * width/height the publisher chose for the embed.
 */
export interface BuildEmbedIframeInput {
  /** height is the iframe height (a CSS length, e.g. "480" or "480px"). */
  height: string;
  /** src is the embed iframe src built by buildEmbedSrc. */
  src: string;
  /** title is the iframe's accessible title. */
  title: string;
  /** width is the iframe width (a CSS length, e.g. "100%" or "640"). */
  width: string;
}

/**
 * escapeHtmlAttribute rewrites a string for safe interpolation into a double-quoted HTML
 * attribute value. The ampersand is replaced first so the entities the later passes introduce
 * are not themselves re-escaped, then the angle brackets and the double quote. The apostrophe
 * is left as-is, since a double-quoted attribute does not require escaping it, so a common name
 * like `Tour de l'Île` stays readable.
 */
const escapeHtmlAttribute = (value: string): string =>
  value.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");

/**
 * buildEmbedIframe renders the copy-paste `<iframe>` HTML a publisher pastes into their site:
 * the src, the chosen width and height, a zero border with rounded corners (8px, the studio's
 * `md` radius, so the embed reads as a finished card on the host page), lazy loading, and an
 * accessible title. Every interpolated value is HTML-escaped, so a title, a width or a height a
 * publisher types cannot break out of its attribute, and the `&` between the src's params is
 * written `&amp;`, which a browser reads back as `&`.
 *
 * It carries no `allow="fullscreen"`: the embedded studio offers no fullscreen affordance, so
 * the permission would be dead. It carries `referrerpolicy="strict-origin-when-cross-origin"`:
 * the hosted embed serves only a frame request that names its page in `Referer`, since that
 * origin is what its allowed-domains check reads, and the attribute sends the page's origin (and
 * never its path) even from a page whose own `Referrer-Policy` is `no-referrer`, which would
 * otherwise leave the embed blank. It is indented for readability because it is copy-paste
 * output a human reads.
 *
 * @example
 * ```ts
 * const html = buildEmbedIframe({
 *   height: "480",
 *   src: "https://mountaya.com/studio/embed?publishable_key=pk_example&itinerary_id=itin-1",
 *   title: "Tour du Mont Blanc",
 *   width: "100%",
 * });
 * ```
 */
export const buildEmbedIframe = (input: BuildEmbedIframeInput): string =>
  [
    "<iframe",
    `  src="${escapeHtmlAttribute(input.src)}"`,
    `  width="${escapeHtmlAttribute(input.width)}"`,
    `  height="${escapeHtmlAttribute(input.height)}"`,
    `  title="${escapeHtmlAttribute(input.title)}"`,
    `  style="border:0;border-radius:${EMBED_IFRAME_RADIUS_PX}px"`,
    '  loading="lazy"',
    '  referrerpolicy="strict-origin-when-cross-origin"',
    "></iframe>",
  ].join("\n");

/**
 * EMBED_SESSION_ENDPOINT is the path the session script asks by default: the endpoint of the
 * integrator's own server that mints the hosted embed's session token. A publisher whose endpoint
 * lives elsewhere edits the one line of the script that states it.
 *
 * It is the embed's own path, and not the `/api/mountaya-session` of the SDK's examples, which
 * mints an Embedded Studio's token: a `studio-embedded` session naming the signed-in member, for
 * the studio's key. The embed's endpoint calls `POST /v1/sessions` with the embed's own
 * publishable key and nothing else, so its token names no surface (the API reads `api`) and no
 * user, and reads what the key alone reaches: what the organization publishes.
 */
export const EMBED_SESSION_ENDPOINT = "/api/mountaya-embed-session";

/** BuildEmbedSessionScriptInput is the input to buildEmbedSessionScript. */
export interface BuildEmbedSessionScriptInput {
  /**
   * appOrigin is the origin the embed is served from, a scheme and a host with no path, as
   * {@link buildEmbedSrc} takes it. The script answers only a frame of that origin, and posts the
   * token to that origin alone.
   */
  appOrigin: string;
  /**
   * endpoint is the path or URL of the integrator's endpoint that mints a session token.
   *
   * @default EMBED_SESSION_ENDPOINT
   */
  endpoint?: string | undefined;
}

/**
 * scriptString renders a value as a JavaScript string literal that cannot close the `<script>`
 * holding it: JSON's own quoting, with every `<` written `\u003c`.
 */
const scriptString = (value: string): string => JSON.stringify(value).replace(/</g, "\\u003c");

/**
 * buildEmbedSessionScript renders the `<script>` an organization that sets
 * `require_session_token` pastes beside the snippet's iframe. A session token lives minutes and
 * only the integrator's server can mint one, so the hosted embed asks the page that frames it
 * for a token, when it loads with none and a minute before the one it holds expires, with a
 * `mountaya:requestSessionToken` message. The script answers each one: it fetches the endpoint,
 * which calls `POST /v1/sessions` with the secret key, and posts the session's `token` and
 * `expires_at` back to the frame that asked as a `mountaya:setSessionToken`. The endpoint may pass
 * the API's answer through as it came, `{ data: { token, expires_at } }`, or answer the session
 * unwrapped, `{ token, expires_at }`: the script reads either.
 *
 * It hears only a frame of the embed's origin, and posts the token to that origin alone, so no
 * other frame on the page is handed one. One copy serves every embed on a page: a second copy
 * pasted beside another iframe attaches nothing. An endpoint that fails, or that answers no token
 * in either shape, is reported on the page's console with nothing posted, and the embed asks
 * again a minute later. So is a session that names a user (`user_id` a string): that is a
 * member's token, minted for an Embedded Studio, which would read the member's drafts in a page
 * any visitor loads, and no embed is handed one. The values it states are JavaScript string
 * literals that cannot close the script. Indented for readability, as the iframe is.
 *
 * @example
 * ```ts
 * const html = buildEmbedSessionScript({ appOrigin: "https://mountaya.com" });
 * ```
 */
export const buildEmbedSessionScript = (input: BuildEmbedSessionScriptInput): string =>
  [
    "<script>",
    "  // Answers each Mountaya embed's request for a session token from your endpoint, which",
    "  // calls POST /v1/sessions with your secret key and answers its body, or the session in it.",
    "  (function () {",
    `    var origin = ${scriptString(originBase(input.appOrigin))};`,
    `    var endpoint = ${scriptString(input.endpoint ?? EMBED_SESSION_ENDPOINT)};`,
    "    if (window.mountayaEmbedSession) return;",
    "    window.mountayaEmbedSession = true;",
    '    window.addEventListener("message", function (event) {',
    "      if (event.origin !== origin || !event.data) return;",
    `      if (event.data.type !== ${scriptString(REQUEST_SESSION_TOKEN_MESSAGE)}) return;`,
    "      var frame = event.source;",
    '      fetch(endpoint, { credentials: "same-origin" })',
    "        .then(function (response) {",
    '          if (!response.ok) throw new Error("Session endpoint answered " + response.status);',
    "          return response.json();",
    "        })",
    "        .then(function (body) {",
    "          var session = body && body.data ? body.data : body;",
    '          if (!session || typeof session.token !== "string") {',
    '            throw new Error("Session endpoint answered no token");',
    "          }",
    '          if (typeof session.user_id === "string") {',
    '            throw new Error("Session endpoint answered a token naming a user: mint without user_id");',
    "          }",
    "          frame.postMessage(",
    `            { type: ${scriptString(SET_SESSION_TOKEN_MESSAGE)}, token: session.token, expiresAt: session.expires_at },`,
    "            origin",
    "          );",
    "        })",
    "        .catch(function (error) {",
    "          console.error(error);",
    "        });",
    "    });",
    "  })();",
    "</script>",
  ].join("\n");
